import type { Palette } from '../pixel/palette';

export type EffectType = 'none' | 'stars' | 'warp' | 'nebula';

// LCG seeded PRNG — stable random positions that don't look patterned
function mkRng(seed: number) {
  let s = seed >>> 0;
  return () => { s = (Math.imul(s, 1664525) + 1013904223) >>> 0; return s / 0x100000000; };
}

const rng1 = mkRng(0xdeadbeef);
const STAR_DATA = Array.from({ length: 110 }, () => ({
  nx: rng1(),
  ny: rng1(),
  layer: Math.floor(rng1() * 3) as 0 | 1 | 2,
}));

const rng2 = mkRng(0xbadc0de);
const NEBULA_DATA = Array.from({ length: 70 }, () => ({
  nx: rng2(),
  ny: rng2(),
  sz: Math.floor(rng2() * 3) + 1,
  alpha: 0.05 + rng2() * 0.15,
  colorIdx: Math.floor(rng2() * 3) as 0 | 1 | 2,
  rate: 0.2 + rng2() * 0.6,
}));

const LAYER_SPEED = [8, 24, 72];      // pixels/sec per layer at speed=1 on 300px canvas
const LAYER_COLOR = ['#1e293b', '#475569', '#94a3b8'];

function drawStars(ctx: CanvasRenderingContext2D, speed: number, t: number, w: number, h: number) {
  const scale = h / 300;
  for (const s of STAR_DATA) {
    const dy = t * LAYER_SPEED[s.layer] * speed * scale;
    const x = Math.floor(s.nx * w);
    const y = Math.floor(((s.ny * h + dy) % h + h) % h);
    ctx.fillStyle = LAYER_COLOR[s.layer];
    if (speed > 1 && s.layer === 2) {
      const len = Math.max(2, Math.floor((speed - 1) / 3 * h / 6));
      ctx.fillRect(x, y, 1, len);
    } else {
      ctx.fillRect(x, y, s.layer === 2 ? 2 : 1, s.layer === 2 ? 2 : 1);
    }
  }
}

function drawWarp(ctx: CanvasRenderingContext2D, speed: number, t: number, w: number, h: number) {
  const cx = w / 2, cy = h / 2;
  const maxR = Math.hypot(cx, cy) * 1.1;
  ctx.lineWidth = 1;
  for (let i = 0; i < 20; i++) {
    const angle = (i / 20) * Math.PI * 2;
    const cos = Math.cos(angle), sin = Math.sin(angle);
    const phase = ((t * speed * 0.7 + i / 20) % 1 + 1) % 1;
    const startR = phase * maxR * 0.04;
    const endR = phase * maxR;
    const alpha = Math.min(phase * 3, (1 - phase) * 2, 1) * Math.min(speed / 2, 0.7);
    if (alpha < 0.02) continue;
    ctx.globalAlpha = alpha;
    ctx.strokeStyle = '#64748b';
    ctx.beginPath();
    ctx.moveTo(cx + cos * startR, cy + sin * startR);
    ctx.lineTo(cx + cos * endR, cy + sin * endR);
    ctx.stroke();
  }
  ctx.globalAlpha = 1;
}

function drawNebula(ctx: CanvasRenderingContext2D, speed: number, t: number, w: number, h: number, palette: Palette) {
  const colors = [palette.accent, palette.glass, palette.accentLight];
  const scale = h / 300;
  for (const n of NEBULA_DATA) {
    const dy = t * n.rate * speed * 10 * scale;
    const x = Math.floor(n.nx * w);
    const y = Math.floor(((n.ny * h + dy) % h + h) % h);
    ctx.globalAlpha = n.alpha * Math.min(speed * 2, 1);
    ctx.fillStyle = colors[n.colorIdx];
    ctx.fillRect(x, y, n.sz, n.sz);
  }
  ctx.globalAlpha = 1;
}

export function drawEffect(
  ctx: CanvasRenderingContext2D,
  type: EffectType,
  speed: number,
  t: number,
  w: number,
  h: number,
  palette: Palette,
): void {
  if (type === 'none') return;
  if (type === 'stars')  drawStars(ctx, speed, t, w, h);
  if (type === 'warp')   drawWarp(ctx, speed, t, w, h);
  if (type === 'nebula') drawNebula(ctx, speed, t, w, h, palette);
}
